import classnames from 'classnames';
import { not } from 'ramda';
import React, { FC, memo, useCallback } from 'react';
import { BiAddMediaActionTarget, BiAddMediaClickOrigin } from '../../../../constants/Bi';
import { DragType } from '../../../../constants/DragType';
import { stopPropagation } from '../../../../utils/dom/domEvents';
import { ActionClickOrigin } from '../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import {
  isItemRenamable,
  isJustCreatedFolderId,
  isWixVideoLibraryLink,
} from '../../../../utils/media-manager-sdk/items';
import { DragSource, DropZone, FolderThumbnail, Input, ItemActionsRow } from '../../../common';
import { ActionButton } from '../../../common/ItemActionsRow/ItemActionsRow.types';
import { GenericItem } from '../../Generic/GenericItem';
import { IFolder } from '../../types';
import baseItemStyles from '../GalleryItemBase.scss';
import { ItemName } from '../ItemName';
import folderStyles from './GalleryFolder.scss';

const actionButtons = [ActionButton.More, ActionButton.AddToBoard];

const accepts = [DragType.Folder, DragType.File];

const GalleryFolderComponent: FC<IFolder> = ({
  item,
  className,
  selected,
  isEditing,
  isCut,
  isSecure,
  onClick,
  onContextMenu,
  onRenameComplete,
  onRenameCancel,
  onNameChange,
  onDoubleClick,
  onDrop,
  beginDrag,
  canDrag,
  selectedItems,
  onRenameStart,
  currentFolder,
  onRemoveItemsFromBoard,
  source,
}) => {
  const handleNameClick = useCallback(
    (event) => {
      if (selected && isItemRenamable(item)) {
        onRenameStart(item.id);
        stopPropagation(event);
      }
    },
    [onRenameStart, selected, item],
  );

  const canShowItemActions = not(isWixVideoLibraryLink(item));

  return (
    <GenericItem
      className={classnames(
        baseItemStyles.item,
        {
          [folderStyles.selected]: selected,
          [folderStyles.isCut]: isCut,
        },
        className,
      )}
      item={item}
      selected={selected}
      onDrop={onDrop}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onRenameComplete={onRenameComplete}
      onNameChange={onNameChange}
      onRenameCancel={onRenameCancel}
      onDoubleClick={onDoubleClick}
      dataHook="gallery-folder"
      canDrag={canDrag}
      selectedItems={selectedItems}
    >
      {({
        handleNameChange,
        handleRenameComplete,
        handleRenameCancel,
        handleDropOnFolder,
        openContextMenu,
        canDrag,
      }) => (
        <DragSource item={item} beginDrag={beginDrag} type={DragType.Folder} disabled={!canDrag}>
          <DropZone
            activeClassName={folderStyles.dropZoneActive}
            accepts={accepts}
            onDrop={handleDropOnFolder}
            disabled={isJustCreatedFolderId(item.id)}
            biOrigin={BiAddMediaClickOrigin.DragAndDrop}
            biTarget={BiAddMediaActionTarget.FolderDragAndDrop}
            item={item}
          >
            <div
              className={classnames(folderStyles.thumbnailWrapper, {
                [folderStyles.selected]: selected,
              })}
            >
              <div className={folderStyles.thumbnailWrapper}>
                <FolderThumbnail className={folderStyles.thumbnail} folder={item} />
              </div>

              <div className={folderStyles.overlay}>
                {canShowItemActions && (
                  <ItemActionsRow
                    onThreeDotsClick={openContextMenu}
                    className={folderStyles.actionsRow}
                    item={item}
                    buttons={actionButtons}
                    isSecure={isSecure}
                    currentFolder={currentFolder}
                    onRemoveItemsFromBoard={onRemoveItemsFromBoard}
                    source={source}
                    actionsClickOrigin={ActionClickOrigin.File}
                  />
                )}
              </div>
            </div>

            {isEditing ? (
              <Input
                value={item.name}
                onChange={handleNameChange}
                onBlur={handleRenameComplete}
                onEnterPressed={handleRenameComplete}
                onEscapePressed={handleRenameCancel}
                autoFocus
                dataHook="gallery-folder-name-edit-input"
              />
            ) : (
              <div className={baseItemStyles.name} data-hook="gallery-folder-name">
                <ItemName onClick={handleNameClick}>{item.name}</ItemName>
              </div>
            )}
          </DropZone>
        </DragSource>
      )}
    </GenericItem>
  );
};

export const GalleryFolder: FC<IFolder> = memo<IFolder>(GalleryFolderComponent);
